import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit
} from '@angular/core';
import { LoginModule } from '@go-modules/components/auth/login';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { LoginEvent } from '@go-modules/components/auth/login/login.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SessionManagerService, sessionManagerToken } from 'go-modules/session-manager/session-manager.service';
import { SanitizerService, sanitizerServiceToken } from 'go-modules/services/xss/sanitizer.service';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AccountLockoutDialogComponent } from '@go-modules/components/dialogs/account-lockout-dialog/account-lockout-dialog.component';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, of } from 'rxjs';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { RegionSelectorService } from '@dashboard/app/services/region-selector.service';
import { RegionSelectorComponent } from '@dashboard/app/components/region-selector/region-selector.component';
import { LoadingModule } from '@go-modules/components/loading/loading.module';
import { ActiveBrandService } from '@go-modules/services/active-brand';
import { ProductTranslationParams } from '@go-modules/interfaces';

@Component({
	selector: 'login-wrapper',
	imports: [
		LoginModule,
		TranslateModule,
		CommonModule,
		MatDialogModule,
		RegionSelectorComponent,
		LoadingModule
	],
	templateUrl: './login-wrapper.component.html',
	styleUrls: ['./login-wrapper.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginWrapperComponent implements OnInit {
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public formError$: BehaviorSubject<string> = new BehaviorSubject(null);
	public productTranslationParams: ProductTranslationParams;

	constructor (
		private authService: NgxAuthService,
		private translateService: TranslateService,
		private dialog: MatDialog,
		private routerService: RouterService,
		private regionSelectorService: RegionSelectorService,
		private activeBrandService: ActiveBrandService,
		@Inject(sessionManagerToken) private sessionManager: SessionManagerService,
		@Inject(sanitizerServiceToken) private sanitizerService: SanitizerService,
		@Inject('Window') private window: Window
	) {}

	public ngOnInit () {
		const { course, invite } = this.routerService.getCurrentQueryParams();
		if ((course || invite) && this.sessionManager.isActive()) {
			this.toSignUp();
		}
		this.productTranslationParams = this.activeBrandService.getTranslationParams();
		this.translateService.get('document-title_login', this.productTranslationParams).subscribe((title: string) => {
			this.window.document.title = title;

			const accountLockTime = this.sessionManager.getAccountLockedTime();
			if (accountLockTime) {
				this.showAccountLockedModal(accountLockTime);
			}
		});
	}

	public get showRegionSelector (): boolean {
		return this.regionSelectorService.showRegionSelector;
	}

	public get isRedirecting (): boolean {
		return this.regionSelectorService.isRedirecting;
	}

	/**
	 * Handles form submit.
	 *
	 * @return {void}
	 */
	public submit (data: LoginEvent) {
		this.formError$.next(null);
		this.loading$.next(true);

		const { course, invite, redirect } = this.routerService.getCurrentQueryParams();
		const options = {
			inviteId: invite || course,
			redirect: redirect ? this.getRedirectUrl(redirect) : undefined
		};

		return this.authService.login(data.username, data.password)
			.pipe(
				switchMap((response) =>
					this.authService.setUpUser(response.user, options)
				),
				tap((state) => {
					if (state) {
						this.routerService.navigateTo(state.name as ROUTE_NAMES, state.params, state.options);
					}
				}),
				catchError((error) => {
					if (error instanceof Error) {
						if(error.message === 'login-accept-invite-fail-message' && this.sessionManager.isActive()) {
							this.toSignUp();
						}

						this.formError$.next(error.message);
						this.loading$.next(false);
						return of(null);
					}

					switch (error.status) {
						case 423:
							const accountLockTime = error.error.remainingAccountLockSeconds;
							const remainingAccountLockTimeMinutes =
								this.sessionManager.convertToMinutes(accountLockTime);
							this.showAccountLockedModal(remainingAccountLockTimeMinutes);
							this.formError$.next('common-validation_user-password-incorrect');
							break;
						case 404:
							this.formError$.next('login-accept-invite-fail-message');
							break;
						case 409:
							this.formError$.next('old-site_pre-existing-lms-account-message');
							break;
						case 410:
							this.formError$.next('common-validation_sso-exists-on-login');
							break;
						case 400:
							this.formError$.next('common-validation_user-password-incorrect');
							break;
						default:
							this.formError$.next('unknown-error-description');
					}

					this.formError$.next(this.translateService.instant(this.formError$.value));
					this.loading$.next(false);
					return of(null);
				})
				// leave as loading on success so it doesnt re-enable button before navigation
			)
			.subscribe();
	}

	/**
	 * Navigate to forgot password page.
	 *
	 * @returns {void}
	 */
	public forgotPassword (): void {
		this.routerService.navigateTo(ROUTE_NAMES.AUTH_FORGOT, {}, { onSameUrlNavigation: 'reload' });
	}

	/**
	 * Navigates to user sign up page.
	 *
	 * @returns {void}
	 */
	public toSignUp (): void {
		const { course, invite } = this.routerService.getCurrentQueryParams();
		if (course || invite) {
			// We have a join link, bypass account type choice because it's
			// a presenter invite link
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_COURSE_CONFIRM, {}, { queryParamsHandling: 'preserve', skipLocationChange: true });
		} else {
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_SIGNUP, {}, { queryParamsHandling: 'preserve', skipLocationChange: true });
		}
	}

	public loginWithSso (provider) {
		const { course, invite, redirect } = this.routerService.getCurrentQueryParams();

		// ensure redirect is absolute not relative
		let absoluteRedirect = redirect;
		if (absoluteRedirect) {
			absoluteRedirect = new URL(redirect, this.window.location.origin).toString();
		}

		return this.authService.getAuthUrl(provider, { course, invite, redirect: absoluteRedirect })
			.subscribe({
				next: ({ url }: { url: string }) => {
					this.window.location.href = url;
				},
				error: () => {
					this.formError$.next(this.translateService.instant('common_an-unexpected-error-occurred'));
				}
			});
	}

	private showAccountLockedModal (remainingTime): void {
		const modalData = {
			message: this.translateService.instant('modal-account-locked-message', {number: remainingTime}),
			forgotPasswordUrl: '/dashboard/auth/forgot'
		};
		AccountLockoutDialogComponent.open(this.dialog, {
			data: modalData
		});
	}

	private getRedirectUrl (redirectUrl: string) {
		return this.sanitizerService.isValidRedirectUrl(redirectUrl) ? redirectUrl : '/dashboard';
	}
}
